import { supabase } from '@core/supabase/client';
import { aiUsageService } from '@core/supabase/services/aiUsageService';

export type LlamaChatMessage = { role: 'system' | 'user' | 'assistant'; content: string };

export type LlamaTokenUsage = {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
};

/** OpenAI-compatible tool call chunk from Groq (Agent panel, function calling). */
export type LlamaToolCall = {
  id: string;
  type: string;
  function: { name: string; arguments: string };
};

export type LlamaInvokeResult = {
  content: string;
  model?: string;
  usage?: LlamaTokenUsage;
  toolCalls?: LlamaToolCall[];
};

/** When set, successful/failed invokes are written to ggj_b2b_ai_usage_log (MAT-050). */
export type InvokeLlamaMeta = {
  orgId: string;
  feature: string;
  orgUserId?: string | null;
};

async function fetchOrgGroqApiKey(orgId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('ggj_b2b_org_integrations')
    .select('settings, is_active')
    .eq('org_id', orgId)
    .eq('provider', 'groq')
    .maybeSingle();

  if (error) {
    // PGRST205: table missing on DB (migrations not applied) — still use Edge GROQ_API_KEY.
    console.warn(
      '[llamaChat] Skipping org Groq integration lookup:',
      error.code ?? '',
      error.message ?? error,
    );
    return null;
  }

  if (!data?.is_active) return null;
  const k = (data.settings as { api_key?: string } | null)?.api_key;
  return typeof k === 'string' && k.trim().length >= 20 ? k.trim() : null;
}

/**
 * **Sync** AI — calls Edge Function `ggj_b2b_llama` → Groq (Llama). Keys: Edge secret and/or Integrations → Groq.
 *
 * **Async** (PRD §10.4): long or batch work should use `aiWorkQueueService.enqueueCandidateSkills` and poll
 * `Candidate.async_ai_work_status`; `ggj_b2b_run-schedulers` drains `ggj_b2b_ai_work_queue` with `GROQ_API_KEY`.
 */
export type InvokeLlamaOptions = {
  /** OpenAI-format tool definitions; forwarded to Groq for native function calling. */
  tools?: unknown[];
};

/**
 * §9.2 — fallback order is Groq → Ollama. When `VITE_AGENT_RUNNER_FALLBACK=ollama`
 * (or unset — ollama is the default fallback) AND Ollama is configured, any
 * edge/Groq failure (network error, empty response, explicit error payload)
 * transparently retries against the local Ollama daemon and returns its
 * content as if Groq had produced it.
 *
 * Tool-calling is Groq-only: if the caller passed `options.tools` the
 * fallback still runs but `toolCalls` will always be undefined in the
 * fallback branch. Callers that rely on tool calls (e.g. Smart Match's
 * AgentThreadColumn) already handle the no-tool-call path.
 */
function ollamaFallbackEnabled(): boolean {
  const raw = import.meta.env.VITE_AGENT_RUNNER_FALLBACK;
  if (raw === 'ollama') return true;
  // Default: ollama fallback ON when no explicit value is set (Groq-first
  // with Ollama safety net is the shipping posture per §9.2).
  if (raw === undefined || raw === null || raw === '') return true;
  return false;
}

async function tryOllamaFallback(
  messages: LlamaChatMessage[],
): Promise<LlamaInvokeResult | null> {
  if (!ollamaFallbackEnabled()) return null;
  const { isOllamaConfigured, invokeOllamaChat } = await import('./ollamaChat');
  if (!isOllamaConfigured()) return null;
  const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user')?.content ?? '';
  console.info(
    `[ollama] → sending prompt to local Ollama (${lastUserMsg.length} chars)`,
    lastUserMsg.slice(0, 120),
  );
  try {
    const res = await invokeOllamaChat(messages);
    console.info(
      `[ollama] ✓ response from local Ollama (model: ${res.model}, ${res.content.length} chars)`,
      res.content.slice(0, 120),
    );
    return { content: res.content, model: `ollama:${res.model}` };
  } catch (err) {
    console.warn(
      '[ollama] ✗ Ollama fallback failed:',
      err instanceof Error ? err.message : err,
    );
    return null;
  }
}

/**
 * Circuit breaker (21-Apr) — `ggj_b2b_llama` has been returning 502s from
 * the Supabase gateway on and off. When that happens `supabase-js`
 * internally `console.error`s every call, and the browser devtools fill
 * with duplicate "POST … 502 (Bad Gateway)" noise. Worse, every paraphrase
 * submit re-triggers the failing call.
 *
 * We track recent failures and, once the threshold trips, short-circuit
 * every subsequent invocation for `OPEN_COOLDOWN_MS`. During that window
 * `invokeLlamaChat` skips the edge call entirely and either returns the
 * Ollama fallback (when configured) or throws a synthetic error — so
 * callers that already have a stub fallback (clarify paraphrase, etc.)
 * degrade silently instead of generating more gateway spam.
 *
 * The breaker is process-scoped (module state); a full page reload resets
 * it, so a user who hits reload after the backend recovers gets the real
 * AI again without waiting for the cooldown.
 */
const BREAKER_FAILURE_THRESHOLD = 2;
const BREAKER_OPEN_COOLDOWN_MS = 60_000;
const BREAKER_MAX_COOLDOWN_MS = 30 * 60_000; // cap rate-limit cooldown at 30m
let edgeFailureCount = 0;
let edgeBreakerOpenUntil = 0;
let edgeBreakerNotified = false;
/**
 * Why the breaker tripped — drives the UI copy ("AI assistant is offline"
 * vs "Groq rate limit — using local AI"). `null` means breaker is closed.
 */
let edgeBreakerReason: 'rate-limit' | 'error' | null = null;

function edgeBreakerIsOpen(): boolean {
  return Date.now() < edgeBreakerOpenUntil;
}

function recordEdgeFailure(reason: string): void {
  edgeFailureCount += 1;
  if (edgeFailureCount >= BREAKER_FAILURE_THRESHOLD && !edgeBreakerIsOpen()) {
    edgeBreakerOpenUntil = Date.now() + BREAKER_OPEN_COOLDOWN_MS;
    edgeBreakerReason = 'error';
    if (!edgeBreakerNotified) {
      console.warn(
        `[llamaChat] ggj_b2b_llama edge function failing (${reason}); ` +
          `skipping for ${Math.round(BREAKER_OPEN_COOLDOWN_MS / 1000)}s`,
      );
      edgeBreakerNotified = true;
    }
  }
}

/**
 * Trip the breaker immediately using the retry-after hint from Groq
 * (parsed server-side in the edge function). Rate-limits are not a
 * transient outage — they're known-bad for N minutes, so we don't bother
 * with the 2-strike count. Cap at 30 min to avoid permanently disabling
 * the edge for a broken header.
 */
function recordEdgeRateLimit(retryAfterMs: number | null, reason: string): void {
  const cooldown = Math.min(
    BREAKER_MAX_COOLDOWN_MS,
    Math.max(BREAKER_OPEN_COOLDOWN_MS, retryAfterMs ?? BREAKER_OPEN_COOLDOWN_MS),
  );
  edgeBreakerOpenUntil = Date.now() + cooldown;
  edgeBreakerReason = 'rate-limit';
  edgeFailureCount = BREAKER_FAILURE_THRESHOLD;
  if (!edgeBreakerNotified) {
    console.warn(
      `[llamaChat] Groq rate limited (${reason}); ` +
        `falling back to Ollama for ${Math.round(cooldown / 1000)}s`,
    );
    edgeBreakerNotified = true;
  }
}

function recordEdgeSuccess(): void {
  edgeFailureCount = 0;
  edgeBreakerOpenUntil = 0;
  edgeBreakerNotified = false;
  edgeBreakerReason = null;
}

/**
 * Exposed so UI surfaces (e.g. Smart Match clarify banner) can render a
 * "AI assistant offline — using local paraphrase" hint when the breaker
 * has tripped.
 */
export function isLlamaEdgeBreakerOpen(): boolean {
  return edgeBreakerIsOpen();
}

/**
 * Why the breaker is currently open — so the UI can show a more specific
 * message for rate limits vs. generic outages. Returns `null` when closed.
 */
export function getLlamaEdgeBreakerReason(): 'rate-limit' | 'error' | null {
  return edgeBreakerIsOpen() ? edgeBreakerReason : null;
}

export async function invokeLlamaChat(
  messages: LlamaChatMessage[],
  model?: string,
  meta?: InvokeLlamaMeta | null,
  options?: InvokeLlamaOptions | null,
): Promise<LlamaInvokeResult> {
  const logFailure = (msg: string) => {
    if (meta?.orgId && meta.feature) {
      void aiUsageService.log({
        orgId: meta.orgId,
        feature: meta.feature,
        status: 'error',
        errorMessage: msg,
        orgUserId: meta.orgUserId,
      });
    }
  };

  // Circuit breaker — skip the edge call if it's been failing consistently.
  // Callers with a stub fallback (clarify paraphrase) will degrade silently;
  // callers without a fallback will see the thrown error just like before.
  if (edgeBreakerIsOpen()) {
    const fallback = await tryOllamaFallback(messages);
    if (fallback) return fallback;
    logFailure('AI edge function temporarily disabled (circuit breaker open)');
    throw new Error('AI edge function temporarily unavailable');
  }

  let groqApiKey: string | undefined;
  if (meta?.orgId) {
    const k = await fetchOrgGroqApiKey(meta.orgId);
    if (k) groqApiKey = k;
  }

  const body: Record<string, unknown> = {
    messages,
    model,
    ...(groqApiKey ? { groq_api_key: groqApiKey } : {}),
  };
  if (options?.tools && options.tools.length > 0) {
    body.tools = options.tools;
  }

  const { data, error } = await supabase.functions.invoke('ggj_b2b_llama', {
    body,
  });

  if (error) {
    recordEdgeFailure(error.message ?? 'invoke error');
    const fallback = await tryOllamaFallback(messages);
    if (fallback) return fallback;
    logFailure(error.message ?? 'AI request failed');
    throw new Error(error.message ?? 'AI request failed');
  }

  const payload = data as {
    content?: string;
    tool_calls?: LlamaToolCall[];
    model?: string;
    error?: string;
    usage?: LlamaTokenUsage;
    /** New (21-Apr): edge function now tags structured rate-limit responses. */
    rate_limited?: boolean;
    retry_after_ms?: number | null;
    fallback_suggested?: string;
    attempts?: Array<{ model: string; error: string; status: number }>;
    fallback_from?: string[];
  } | null;

  if (payload?.error) {
    if (payload.rate_limited) {
      recordEdgeRateLimit(payload.retry_after_ms ?? null, payload.error);
    } else {
      recordEdgeFailure(payload.error);
    }
    const fallback = await tryOllamaFallback(messages);
    if (fallback) return fallback;
    logFailure(payload.error);
    throw new Error(payload.error);
  }

  const toolCalls = Array.isArray(payload?.tool_calls) ? payload!.tool_calls! : undefined;
  const content = typeof payload?.content === 'string' ? payload.content : '';

  if (!content.trim() && (!toolCalls || toolCalls.length === 0)) {
    recordEdgeFailure('empty response');
    const fallback = await tryOllamaFallback(messages);
    if (fallback) return fallback;
    logFailure('Empty AI response');
    throw new Error('Empty AI response');
  }

  recordEdgeSuccess();

  const result: LlamaInvokeResult = {
    content,
    model: payload?.model,
    usage: payload?.usage,
    ...(toolCalls && toolCalls.length > 0 ? { toolCalls } : {}),
  };

  if (meta?.orgId && meta.feature) {
    const pt = result.usage?.prompt_tokens ?? null;
    const ct = result.usage?.completion_tokens ?? null;
    const totalTok =
      result.usage?.total_tokens ??
      (pt != null || ct != null ? (pt ?? 0) + (ct ?? 0) : null);
    void aiUsageService.log({
      orgId: meta.orgId,
      feature: meta.feature,
      model: result.model ?? null,
      promptTokens: pt,
      completionTokens: ct,
      creditsUsed:
        totalTok != null && totalTok > 0 ? Number((totalTok * 1e-6).toFixed(8)) : null,
      status: 'ok',
      orgUserId: meta.orgUserId,
    });
  }

  return result;
}

/** @deprecated use invokeLlamaChat */
export const invokeRequisitionLlama = invokeLlamaChat;

/** One skill per line; strips common bullets (requisitions AI apply). */
export function parseSkillLines(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((line) =>
      line
        .replace(/^[\s•\-\*\d\.\)]+/i, '')
        .trim()
    )
    .filter((s) => s.length > 1 && s.length < 120);
}
